import styled from "styled-components";
import { Box, BoxProps } from "../Box";

/**
 * Shared read-only field for detail / view screens.
 *
 * Replaces the ad-hoc `<Heading>Label</Heading> + <CardLine>value</CardLine>`
 * pattern, which rendered the label larger/darker than the value (inverted
 * hierarchy). Here the value leads (prominent, dark) and the label supports
 * (small, uppercase, muted) — crisper and more scannable.
 */
interface DetailFieldProps extends BoxProps {
  label: string;
  /** Value to render. Falsy values fall back to an em-dash. */
  value?: React.ReactNode;
  children?: React.ReactNode;
  size?: "sm" | "md";
  /** Lay label and value side-by-side instead of stacked. */
  inline?: boolean;
}

const FieldLabel = styled.span`
  ${({ theme: { colors, fontWeights, letterSpacings } }) => `
      display: block;
      font-family: "Inter", sans-serif;
      font-size: 12px;
      font-weight: ${fontWeights.semibold};
      letter-spacing: ${letterSpacings.wide};
      text-transform: uppercase;
      color: ${colors.gray[50]};
  `}
`;

const FieldValue = styled.div<{ size: "sm" | "md" }>`
  ${({ theme: { colors, fontWeights }, size }) => `
      font-family: "Inter", sans-serif;
      font-size: ${size === "sm" ? "13px" : "15px"};
      font-weight: ${fontWeights.medium};
      line-height: 1.4;
      color: ${colors.darkBlue};
      word-break: break-word;
  `}
`;

export const DetailField = (props: DetailFieldProps) => {
  const { label, value, children, size = "md", inline, ...styleProps } = props;
  const content = children ?? value;
  const display =
    content === null || content === undefined || content === "" ? "—" : content;

  if (inline) {
    return (
      <Box display="flex" alignItems="baseline" style={{ gap: "8px" }} {...styleProps}>
        <FieldLabel>{label}</FieldLabel>
        <FieldValue size={size}>{display}</FieldValue>
      </Box>
    );
  }

  return (
    <Box {...styleProps}>
      <FieldLabel>{label}</FieldLabel>
      <Box mt="1">
        <FieldValue size={size}>{display}</FieldValue>
      </Box>
    </Box>
  );
};
